import { useState } from "react";
import {
    Alert, AlertTitle, Box, Button, Chip, CircularProgress,
    IconButton, Table, TableBody, TableCell, TableContainer,
    TableHead, TablePagination, TableRow, Tooltip,
} from "@mui/material";
import { Edit, Delete } from "@mui/icons-material";
import { ActionDto, ConditionDto, IRuleDto } from "../api/generated";
import { useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

interface RulesTableProps {
    rules: IRuleDto[] | undefined;
    isLoading: boolean;
    isError: boolean;
    error: unknown;
    onEdit: (row: IRuleDto) => void;
    onDelete: (row: IRuleDto) => void;
    stretch?: boolean;
}

const truncate = (v: string | undefined, max = 28) =>
    !v ? "" : v.length <= max ? v : v.slice(0, max - 1) + "…";

const CONDITION_COLORS: Record<string, { bg: string; color: string }> = {
    extension:    { bg: 'rgba(37,99,235,0.08)',  color: '#2563EB' },
    namecontains: { bg: 'rgba(139,92,246,0.08)', color: '#7C3AED' },
    mimetype:     { bg: 'rgba(14,165,233,0.08)', color: '#0284C7' },
    regex:        { bg: 'rgba(245,158,11,0.08)', color: '#D97706' },
};

const ACTION_COLORS: Record<string, { bg: string; color: string }> = {
    move:   { bg: 'rgba(37,99,235,0.1)',  color: '#2563EB' },
    copy:   { bg: 'rgba(14,165,233,0.1)', color: '#0284C7' },
    delete: { bg: 'rgba(239,68,68,0.1)',  color: '#DC2626' },
    rename: { bg: 'rgba(245,158,11,0.1)', color: '#D97706' },
};

const chipSx = { height: 22, borderRadius: 1, fontWeight: 600, fontSize: '0.7rem', border: 'none' };

const headerCellSx = {
    fontWeight: 700,
    color: '#64748B',
    fontSize: '0.72rem',
    textTransform: 'uppercase' as const,
    letterSpacing: '0.05em',
    bgcolor: '#F8FAFC',
    borderBottom: '1px solid #E2E8F0',
};

export default function RulesTable({ rules, isLoading, isError, error, onEdit, onDelete, stretch }: RulesTableProps) {
    const queryClient = useQueryClient();
    const { t } = useTranslation();
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(25);

    const validRules = Array.isArray(rules) ? rules.filter(r => r.id != null && r.id !== "") : [];

    if (isLoading)
        return (
            <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
                <CircularProgress />
            </Box>
        );

    if (isError)
        return (
            <Alert
                severity="error"
                action={
                    <Button color="inherit" size="small" onClick={() => queryClient.invalidateQueries({ queryKey: ["rules"] })}>
                        {t('common.retry')}
                    </Button>
                }
            >
                <AlertTitle>{t('rules.table.loadError')}</AlertTitle>
                {(error as any)?.message ?? t('rules.table.loadErrorMsg')}
            </Alert>
        );

    if (validRules.length === 0)
        return (
            <Box sx={{ py: 6, textAlign: 'center' }}>
                <Box sx={{ color: '#CBD5E1', fontSize: '0.9rem' }}>
                    {t('rules.table.empty')}
                </Box>
            </Box>
        );

    const paged = validRules.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
    const stretchSx = stretch ? { flex: 1, display: 'flex', flexDirection: 'column' as const, overflow: 'hidden', minHeight: 0 } : {};

    return (
        <Box sx={stretchSx}>
            <TableContainer sx={stretch ? { flex: 1, overflow: 'auto' } : {}}>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell sx={headerCellSx}>{t('rules.table.name')}</TableCell>
                            <TableCell sx={headerCellSx}>{t('rules.table.description')}</TableCell>
                            <TableCell sx={{ ...headerCellSx, width: 90 }}>{t('rules.table.priority')}</TableCell>
                            <TableCell sx={{ ...headerCellSx, width: 100 }}>{t('rules.table.status')}</TableCell>
                            <TableCell sx={headerCellSx}>{t('rules.table.conditions')}</TableCell>
                            <TableCell sx={headerCellSx}>{t('rules.table.action')}</TableCell>
                            <TableCell sx={{ ...headerCellSx, width: 96 }} />
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {paged.map((r) => (
                            <TableRow key={r.id} sx={{ '&:hover': { bgcolor: '#F8FAFC' } }}>
                                <TableCell sx={{ fontWeight: 500, color: '#1E293B', maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                    {r.name}
                                </TableCell>
                                <TableCell sx={{ color: '#64748B', fontSize: '0.875rem', maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                    {r.description || '—'}
                                </TableCell>
                                <TableCell sx={{ fontWeight: 600, color: '#64748B', fontSize: '0.875rem' }}>
                                    {r.priority ?? 0}
                                </TableCell>
                                <TableCell>
                                    <Chip
                                        size="small"
                                        label={r.enabled ? t('rules.table.active') : t('rules.table.inactive')}
                                        sx={{
                                            ...chipSx,
                                            bgcolor: r.enabled ? 'rgba(16,185,129,0.1)' : 'rgba(203,213,225,0.3)',
                                            color: r.enabled ? '#10B981' : '#94A3B8',
                                        }}
                                    />
                                </TableCell>
                                <TableCell>
                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                        {(r.conditions ?? []).length === 0 ? (
                                            <Box sx={{ color: '#CBD5E1', fontSize: '0.8rem' }}>—</Box>
                                        ) : (r.conditions ?? []).map((c: ConditionDto, i: number) => {
                                            const style = CONDITION_COLORS[(c.type ?? "").toLowerCase()] ?? { bg: 'rgba(100,116,139,0.08)', color: '#64748B' };
                                            return (
                                                <Tooltip key={i} title={`${c.type}: ${c.value}`} arrow>
                                                    <Chip size="small" label={truncate(c.value, 18)} sx={{ ...chipSx, bgcolor: style.bg, color: style.color, maxWidth: 120 }} />
                                                </Tooltip>
                                            );
                                        })}
                                    </Box>
                                </TableCell>
                                <TableCell>
                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                        {(r.actions ?? []).length === 0 ? (
                                            <Box sx={{ color: '#CBD5E1', fontSize: '0.8rem' }}>—</Box>
                                        ) : (r.actions ?? []).map((a: ActionDto, i: number) => {
                                            const style = ACTION_COLORS[(a.type ?? "").toLowerCase()] ?? { bg: 'rgba(100,116,139,0.1)', color: '#64748B' };
                                            const label = a.target ? `${a.type} → ${truncate(a.target, 16)}` : a.type;
                                            return (
                                                <Tooltip key={i} title={a.target ? `${a.type} → ${a.target}` : (a.type ?? '')} arrow>
                                                    <Chip size="small" label={label} sx={{ ...chipSx, bgcolor: style.bg, color: style.color, maxWidth: 170 }} />
                                                </Tooltip>
                                            );
                                        })}
                                    </Box>
                                </TableCell>
                                <TableCell>
                                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                                        <Tooltip title={t('rules.table.tooltipEdit')} arrow>
                                            <IconButton size="small" onClick={() => onEdit(r)} sx={{ color: '#2563EB', '&:hover': { bgcolor: 'rgba(37,99,235,0.08)' } }}>
                                                <Edit sx={{ fontSize: 17 }} />
                                            </IconButton>
                                        </Tooltip>
                                        <Tooltip title={t('rules.table.tooltipDelete')} arrow>
                                            <IconButton size="small" onClick={() => onDelete(r)} sx={{ color: '#EF4444', '&:hover': { bgcolor: 'rgba(239,68,68,0.08)' } }}>
                                                <Delete sx={{ fontSize: 17 }} />
                                            </IconButton>
                                        </Tooltip>
                                    </Box>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
            <TablePagination
                component="div"
                count={validRules.length}
                page={page}
                rowsPerPage={rowsPerPage}
                onPageChange={(_, p) => setPage(p)}
                onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
                rowsPerPageOptions={[10, 25, 50]}
                labelRowsPerPage={t('common.rowsPerPage')}
                labelDisplayedRows={({ from, to, count }) => t('common.displayedRows', { from, to, count })}
                sx={{ borderTop: '1px solid #E2E8F0', bgcolor: '#F8FAFC', flexShrink: 0 }}
            />
        </Box>
    );
}
